import { useCallback, useEffect, useRef } from "react";

const FOCUSABLE =
	'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

/**
 * Focus handling for a non-modal overlay panel (the Testing session / inspector drawers).
 *
 * Three things a keyboard user needs and an unmanaged overlay does not give them:
 *   1. focus moves INTO the panel when it opens, so the next Tab lands inside it rather than
 *      continuing through the page behind;
 *   2. Tab is contained while it is open, so focus cannot wander behind the scrim;
 *   3. focus RETURNS to whatever opened it on close, instead of resetting to the top of the document.
 *
 * Deliberately not a `role="dialog"` trap: these panels are navigation surfaces, not modals — they
 * do not take an accessible-modal contract, and Escape (owned by the shell) closes them.
 */
export function useOverlayFocus(open: boolean, returnFocusTo?: () => HTMLElement | null) {
	const ref = useRef<HTMLDivElement>(null);
	const restoreTo = useRef<HTMLElement | null>(null);

	const focusables = useCallback(
		() =>
			Array.from(ref.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []).filter(
				(el) => el.offsetParent !== null,
			),
		[],
	);

	// Remember the opener, then move focus in.
	useEffect(() => {
		if (!open) return;
		restoreTo.current = document.activeElement as HTMLElement | null;
		const frame = requestAnimationFrame(() => {
			const first = focusables()[0];
			(first ?? ref.current)?.focus();
		});
		return () => cancelAnimationFrame(frame);
	}, [open, focusables]);

	// Restore on close. The remembered node is often GONE by now — the control that opens a panel is
	// typically only rendered while that panel is closed, so it unmounts on open and a fresh element
	// takes its place. `returnFocusTo` re-finds the current one; the remembered node is the fallback.
	useEffect(() => {
		if (open) return;
		const remembered = restoreTo.current;
		restoreTo.current = null;
		if (!remembered) return;
		// Don't yank focus away from wherever the user deliberately went.
		const active = document.activeElement;
		if (active && active !== document.body && !ref.current?.contains(active)) return;
		// Wait a frame: the reveal control re-mounts in the same commit that closes the panel.
		const frame = requestAnimationFrame(() => {
			const target =
				returnFocusTo?.() ?? (document.body.contains(remembered) ? remembered : null);
			target?.focus();
		});
		return () => cancelAnimationFrame(frame);
	}, [open, returnFocusTo]);

	// Contain Tab while open.
	useEffect(() => {
		if (!open) return;
		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key !== "Tab") return;
			const items = focusables();
			if (items.length === 0) return;
			const first = items[0];
			const last = items[items.length - 1];
			const active = document.activeElement as HTMLElement | null;
			if (!ref.current?.contains(active)) {
				e.preventDefault();
				(e.shiftKey ? last : first).focus();
				return;
			}
			if (!e.shiftKey && active === last) {
				e.preventDefault();
				first.focus();
			} else if (e.shiftKey && active === first) {
				e.preventDefault();
				last.focus();
			}
		};
		document.addEventListener("keydown", onKeyDown);
		return () => document.removeEventListener("keydown", onKeyDown);
	}, [open, focusables]);

	return ref;
}
